import * as React from 'react';
import {
    allSupportedIntegrationTypesForCapability,
    comingSoonIntegrationsForCapability,
    displayTextForCapability,
    isIntegrationBuildDistroSupported,
} from '../../helpers';
import { IntegrationCapability, IntegrationType } from '../../types';
import { antGrey } from '../styles/globalStyles';
import { IntegrationCard } from './IntegrationCard';
import { ComingSoonIntegrationCard } from './ComingSoonIntegrationCard';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import Text from 'antd/lib/typography/Text';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppPlatform, selectIsRunwayOrg } from '../../selectors/selectors';
import { SetupStepsCallerContext } from '../../typescriptHelpers';
import { CreateIntegrationRequestDataType } from '../../actions/integrations';

interface CapabilitySectionProps {
    appId: string;
    orgId: string;
    capability: IntegrationCapability;
    onClickIntegrationShowInstallationModal: (integrationType: IntegrationType) => void;
    onClickShowProviderModal: (integrationType: IntegrationType) => void;
    addIntegration: (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => void;
    shouldShowTitle?: boolean;
    filterIntegrations?: (integration: IntegrationType) => boolean;
    context?: SetupStepsCallerContext;
}

export const CapabilitySection = (props: CapabilitySectionProps) => {
    const {
        appId,
        orgId,
        capability,
        onClickIntegrationShowInstallationModal,
        onClickShowProviderModal,
        addIntegration,
        shouldShowTitle,
        filterIntegrations,
        context,
    } = props;
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    let integrationsForCapability = allSupportedIntegrationTypesForCapability(
        capability,
        isRunwayOrg,
        orgId,
        true,
        platform,
    );

    if (filterIntegrations) {
        integrationsForCapability = integrationsForCapability.filter(filterIntegrations);
    }

    const capabilityDisplayName = displayTextForCapability(capability);
    const comingSoonIntegrations = comingSoonIntegrationsForCapability(capability, isRunwayOrg, orgId);

    const isBuildSystemCapabilityBuildDistroContext =
        capability === IntegrationCapability.BuildSystem &&
        context === SetupStepsCallerContext.BuildDistroConnectCICDIntegration;

    if (isBuildSystemCapabilityBuildDistroContext) {
        // sort integrations so supported ones are at the top
        integrationsForCapability.sort((a) => (isIntegrationBuildDistroSupported(a) ? -1 : 1));
    }

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
                {shouldShowTitle && (
                    <PlanRestrictedFeatureWrapper
                        integrationCapability={capability}
                        requiresTooltip
                        shouldFakeDisabledState
                    >
                        <Text style={{ fontSize: 24, fontWeight: 600, color: antGrey.grey9 }}>
                            {capabilityDisplayName}
                        </Text>
                    </PlanRestrictedFeatureWrapper>
                )}
                <PlanRestrictedFeatureWrapper integrationCapability={capability} shouldFakeDisabledState>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                        {integrationsForCapability.map((integrationType) => {
                            const isComingSoonIntegration = comingSoonIntegrations.includes(integrationType);
                            const disableIntegrationCard =
                                isBuildSystemCapabilityBuildDistroContext ?
                                    !isIntegrationBuildDistroSupported(integrationType)
                                :   false;

                            return isComingSoonIntegration ?
                                    <ComingSoonIntegrationCard
                                        orgId={orgId}
                                        appId={appId}
                                        integrationType={integrationType}
                                    />
                                :   <IntegrationCard
                                        key={integrationType}
                                        integrationType={integrationType}
                                        isConnected={false}
                                        onClickIntegrationShowInstallationModal={
                                            onClickIntegrationShowInstallationModal
                                        }
                                        onClickShowProviderModal={onClickShowProviderModal}
                                        addIntegration={addIntegration}
                                        appId={appId}
                                        orgId={orgId}
                                        disabled={disableIntegrationCard}
                                        context={context}
                                    />;
                        })}
                    </div>
                </PlanRestrictedFeatureWrapper>
            </div>
        </div>
    );
};
